import React, { useEffect, useState } from "react";
import { FormControl, FormLabel, FormErrorMessage } from "@chakra-ui/react";
import Select from "react-select";
import {
  white,
  newBorder,
  textPrimary,
  textSecondary,
  primary,
  hoverItemColor,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { listServiceGroups } from "../../actions/serviceGroupActions";
import { shade } from "polished";
import { useParams } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";

const FormControlServiceGroup = ({
  slug,
  label,
  labelFontSize,
  value,
  onChange,
  isRequired,
  isTouched,
  isError,
  isMulti,
  errorMessage,
  w,
  isSmall,
}) => {
  //TRANSLATION
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // REDUX
  const dispatch = useDispatch();

  const serviceGroupList = useSelector((state) => state.serviceGroupList);
  const { serviceGroups } = serviceGroupList;

  // CONSTANTS
  const [serviceGroupOptions, setServiceGroupOptions] = useState([]);

  // USE EFFECT LOAD SERVICE GROUPS
  useEffect(() => {
    // Dispatch the action with fetchAll set to true
    dispatch(listServiceGroups(slug, "", "", "", "", "", true));
  }, [dispatch, slug]);

  // USE EFFECT TRANSFORM SERVICE GROUPS FOR REACT SELECT AND SET OPTIONS, EXCLUDE INACTIVE SERVICE GROUPS
  useEffect(() => {
    if (serviceGroups && serviceGroups.length > 0) {
      const activeServiceGroups = serviceGroups
        .filter((group) => group.status !== "inactive")
        .sort((a, b) => a.name.localeCompare(b.name)); // <-- sort alphabetically A–Z

      const transformedServiceGroups = activeServiceGroups.map((group) => ({
        value: group._id,
        label: group.name,
        status: group.status,
      }));

      setServiceGroupOptions(transformedServiceGroups);
    }
  }, [serviceGroups]);

  // Custom styles for react-select
  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: "#718096", // Change this to your desired placeholder color
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: isError ? "red" : state.isFocused ? newBorder : newBorder,
      backgroundColor: white,
      border: `1px solid ${newBorder}`,
      fontSize: isSmall ? "0.875rem" : isMobile ? "1rem" : "0.938rem",
      fontWeight: "500",
      minHeight: isSmall ? "2.5rem" : "3rem",
      height: isMulti ? "initial" : isSmall ? "2.5rem" : "3rem",
      margin: "0",
      padding: "0 8px",
      borderRadius: "0.75rem",

      ":focus-visible": {
        borderColor: primary,
        boxShadow: `0 0 0 1px ${newBorder}`,
      },
      ":hover": {
        boxShadow: "none",
        outline: "none",
        cursor: "pointer",
        borderColor: "#cbd5e0",
      },
    }),

    menu: (provided) => ({
      ...provided,
      borderRadius: "0.75rem",
      boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.3)",
      border: `0px solid ${newBorder}`,
      overflow: "hidden",
      zIndex: 90000,
    }),

    menuList: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
      border: "none",
      boxShadow: "none",
    }),

    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.5 : 1,
      color: textPrimary,
      margin: "0",
      padding: "0 0px",

      ":hover": {
        bg: "red",
      },
    }),

    option: (provided, state) => ({
      ...provided,

      // backgroundColor: state.isFocused ? backgroundLight : null,
      backgroundColor: state.isSelected ? primary : white, // Background color of selected option
      // color: state.isFocused ? textPrimary : state.data.color,
      color: state.isSelected ? "white" : textSecondary, // Color of text in options
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      margin: "0",
      padding: "0 1rem",
      fontWeight: "500",
      height: "48px",
      fontSize: "0.875rem",
      borderBottom: `1px solid ${newBorder}`,
      ":hover": {
        backgroundColor: state.isSelected
          ? shade(0.1, primary)
          : hoverItemColor,
        color: state.isSelected ? "white" : textPrimary,
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: primary,
      color: white,
      fontSize: "0.938rem",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: state.data.color,
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      ":hover": {
        backgroundColor: state.data.bgDark,
        color: state.data.color,
      },
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: "11px",
      color: textSecondary,
      fontWeight: "500",
    }),
  };

  // LOG
  // console.log("serviceGroupOptions", serviceGroupOptions);

  return (
    <FormControl
      mb={4}
      isRequired={isRequired}
      isInvalid={isTouched && isError}
      w={w}
    >
      <FormLabel
        fontSize={labelFontSize ? labelFontSize : "sm"}
        position="relative"
      >
        {label}
        {isError && isTouched && (
          <div>
            <FormErrorMessage fontSize="xs">{errorMessage}</FormErrorMessage>
          </div>
        )}
      </FormLabel>

      <Select
        isSearchable
        value={serviceGroupOptions.find((option) => option.value === value)}
        onChange={onChange}
        options={serviceGroupOptions}
        placeholder={t("placeholder.selectServiceGroup")}
        styles={customStyles}
      />
    </FormControl>
  );
};

export default FormControlServiceGroup;
